/**
 * IMPORTANT: DO NOT CHANGE THIS FILE UNTIL DEPRECATED TRIP CART IS REMOVED
 */
import { CalendarSeasons, FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import {
  DEFAULT_TRIP_CART_CAPACITY,
  FORM_CAPTAIN_OPTION,
  FORM_CAPTAIN_OPTIONS,
  FORM_DURATION,
  FORM_GUESTS,
  FORM_SELECTED_DATES,
  FORM_STATE_VALID,
  FORM_WITH_CAPTAIN,
  FULL_DAY_HOURS,
  IS_MULTI_DAY,
} from 'src/listing/constants';
import { BookingMotivatorsState } from 'src/common/providers/useBookingMotivators';
import {
  clampDuration,
  findClosestPriceTierForDuration,
  getDefaultTodayDate,
  getDurationDigits,
  getDurationHours,
  getMinMaxHoursForPreferredPriceTier,
} from 'src/listing/helper';
import newTZDate, { todayDate } from 'src/common/utils/dateHelpers';
import {
  addDays, addMonths, endOfMonth, isBefore,
  startOfMonth,
} from 'date-fns';
import {
  BOOKING_MOTIVATOR_STATES,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE,
} from 'src/common/constants';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { TripCartFormState } from 'src/listing/types';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import { formatDurationUnit } from 'src/trip-cart/utils/tripCartUtils';
import { DurationUnit } from 'src/common/types/api/listings';
import { FORM_DEPARTURE_TIME } from 'src/inquiry/constants';

export const getInitialDurationValue = (
  listing: FullBoatSearchDetail,
  preferredDuration?: string,
): string => {
  if (preferredDuration) {
    const priceTier = findClosestPriceTierForDuration(listing.prices, preferredDuration);
    if (priceTier) {
      const minMaxHours = getMinMaxHoursForPreferredPriceTier([priceTier]);
      const durationValue = minMaxHours
        ? clampDuration(
          preferredDuration,
          minMaxHours.min,
          minMaxHours.max,
        )
        : preferredDuration;
      return durationValue;
    }
  }
  // Prefer hourly if available
  const hourlyPrices = listing.prices.filter((price) => price.price_unit === 'hour');
  if (hourlyPrices.length) {
    const price = hourlyPrices[0];
    return formatDurationUnit(price.min_unit_quantity, price.price_unit as DurationUnit);
  }

  const multiDayPrices = listing.prices.filter((price) => price.price_unit === 'day' || price.price_unit === 'night');
  if (multiDayPrices.length) {
    const price = multiDayPrices[0];
    const minValue = price.min_unit_quantity;
    return formatDurationUnit(minValue, price.price_unit as DurationUnit);
  }

  return '';
};

export const getInitialDatesFromMotivators = (
  bookingMotivators: BookingMotivatorsState,
): Date[] => {
  const unavailableKeys = Object.keys(bookingMotivators).filter((key) => bookingMotivators[key].includes('UNAVAILABLE'));
  return getDefaultTodayDate(unavailableKeys);
};

type AvailableDatesForTripCart = {
  dates: Date[]; // YYYY-MM-DD
  isValid: boolean;
};
const getAvailabilityForDates = ({
  nextAvailableDate,
  preferredDates,
  bookingMotivators,
  isMultiDay,
}: {
  nextAvailableDate?: string;
  preferredDates?: string[];
  bookingMotivators: BookingMotivatorsState;
  isMultiDay: boolean;
}): AvailableDatesForTripCart => {
  const unavailableKeys = Object.keys(bookingMotivators)
    .filter((key) => {
      if (isMultiDay) {
        return bookingMotivators[key].includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE)
        || bookingMotivators[key].includes(BOOKING_MOTIVATOR_STATES.OFF_SEASON);
      }
      return bookingMotivators[key].includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE)
    || bookingMotivators[key].includes(BOOKING_MOTIVATOR_STATES.OFF_SEASON)
    || bookingMotivators[key].includes(BOOKING_MOTIVATOR_STATES.NO_SAME_DAY_PRICING);
    });

  if (preferredDates?.length) {
    const [start] = preferredDates;
    if (!unavailableKeys.includes(start)) {
      return {
        dates: [newTZDate(start)],
        isValid: true,
      };
    }
  }

  const startDate = preferredDates?.length ? newTZDate(preferredDates[0]) : todayDate();
  if (!nextAvailableDate) {
    return {
      dates: [startDate],
      isValid: false,
    };
  }

  const nextMonth = addMonths(startOfMonth(startDate), 1);
  const endDate = endOfMonth(nextMonth);
  const nextDate = newTZDate(nextAvailableDate);
  if (isBefore(nextDate, endDate)) {
    return {
      dates: [nextDate],
      isValid: true,
    };
  }

  return {
    dates: [startDate],
    isValid: false,
  };
};

/**
 * Booking motivators should not be added in the dependency list of dates
 * because new motivators will be loaded and merged. To avoid re-calculation
 * of dates here, booking motivators should be ignored.
 */
export const getInitialDates = ({
  bookingMotivators,
  preferredDates,
  isMultiDay,
  listing,
  duration,
}: {
  bookingMotivators: BookingMotivatorsState;
  preferredDates?: string[];
  isMultiDay: boolean;
  listing: FullBoatSearchDetail;
  duration: string;
}): {
  defaultDates: Date[];
  isDatesValid: boolean;
} => {
  const {
    next_available_date: nextAvailableDate,
  } = listing;
  const calendarSeasons = listing?.calendar_seasons ?? {} as CalendarSeasons;
  const multidayPricingUnit = calendarSeasons?.multiday_pricing_unit ?? 'day';
  const { dates: indicatorDefaultDates, isValid: isDatesValid } = getAvailabilityForDates({
    bookingMotivators,
    isMultiDay,
    nextAvailableDate,
    preferredDates,
  });
  if (!isMultiDay) {
    return { defaultDates: indicatorDefaultDates, isDatesValid };
  }

  let defaultDates: Date[] = [];
  const isDay = multidayPricingUnit === 'day';
  const durationValue = getDurationDigits(duration) - (isDay ? 1 : 0);

  const [start] = indicatorDefaultDates;
  defaultDates = [start, addDays(start, durationValue)];

  if (durationValue === 0) {
    defaultDates.length = 1;
  }

  return { defaultDates, isDatesValid };
};

export const getInitialGuestCount = (
  listing: FullBoatSearchDetail,
  preferredCapacity?: Partial<RecentInquiryData>['capacity'],
): number => {
  let preferred = Number(preferredCapacity);
  if (Number.isNaN(preferred) || preferred < 1) preferred = DEFAULT_TRIP_CART_CAPACITY;
  const guests = Math.min(
    preferred,
    listing.capacity,
  );
  return guests;
};

/**
 * `listing.captain_options` can only be `[1]` or `[2]` or `[2, 4]`
 * When `[2, 4]`, a single option can be derived by checking `listing.captained`
 */
export const getInitialCaptainValue = (
  listing: FullBoatSearchDetail,
  preferredWithCaptain?: boolean,
): CaptainOptionValue => {
  // Nothing to compare, use listing as is
  if (listing.captain_options.length === 1) {
    return listing.captain_options[0];
  }

  if (preferredWithCaptain != null) {
    if (!preferredWithCaptain) {
      return CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE;
    }
    // Match to listing
    return listing.captain_options.find((option) => (
      option !== CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE
      // fallback shouldn't be needed, used for data issues
    )) ?? CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE;
  }

  // Has multiple options: 2 and 4. Prefer 2
  return CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN;
};

/**
 * The duration must be validated against prices beforehand
 */
export const getInitialMultiDay = (
  listing: FullBoatSearchDetail,
  duration: string,
): boolean => {
  const durationHours = getDurationHours(duration);

  // Only when greater than "full day" (24H). Full day will show on hourly side if !isOnlyMultiday
  // If it contains "night" then, full day isn't possible
  if (durationHours > FULL_DAY_HOURS) {
    return true;
  }

  if (durationHours === FULL_DAY_HOURS) {
    // full day is same day
    if (listing.calendar_seasons?.multiday_pricing_unit === 'day') {
      return false;
    }

    // nights
    return true;
  }

  // same day (hourly and full day)
  return false;
};

type GetInitialValues = {
  listing: FullBoatSearchDetail;
  bookingMotivators?: BookingMotivatorsState;
  tripCartCookieData?: Partial<RecentInquiryData>;
};

/**
 * Form values before instabook trips have been retrieved
 */
export const getInitialValues = ({
  listing,
  bookingMotivators,
  tripCartCookieData,
}: GetInitialValues): TripCartFormState => {
  const duration = tripCartCookieData?.duration ?? '';
  // Infer multiday from duration
  const isMultiDay = getInitialMultiDay(listing, duration);
  const { defaultDates: dates, isDatesValid } = getInitialDates({
    duration,
    bookingMotivators: bookingMotivators ?? {},
    preferredDates: tripCartCookieData?.dates,
    isMultiDay,
    listing,
  });
  const withCaptain = getInitialCaptainValue(listing, tripCartCookieData?.captained);
  const guests = getInitialGuestCount(listing, tripCartCookieData?.capacity);

  const formInitialValues: TripCartFormState = {
    [FORM_SELECTED_DATES]: dates,
    [FORM_GUESTS]: guests,
    [IS_MULTI_DAY]: isMultiDay,
    [FORM_DURATION]: duration,
    [FORM_WITH_CAPTAIN]: withCaptain,
    [FORM_CAPTAIN_OPTION]: listing.captain_option ?? '',
    [FORM_CAPTAIN_OPTIONS]: listing.captain_options,
    [FORM_DEPARTURE_TIME]: undefined,
    [FORM_STATE_VALID]: isDatesValid,
  };

  return formInitialValues;
};
